import React from 'react'
import { Form, Input, InputNumber, Select, Button, Row, Col, Tooltip, message, Progress } from 'antd'
import { AppSettings } from '../../appSettings'
import { FormComponentProps } from 'antd/lib/form'
import { getAidraRecommendation } from 'services/CeberusApiService'
import CloudRecommendationResult from 'components/CloudRecommendationResult'

interface Props extends FormComponentProps {}

interface State {
  loading: boolean
  response: any
  clusters?: any[]
  defaultCluster?: string
  loadingPercent?: number
}

class CloudRecommendationBase extends React.Component<Props, State> {
  private loaderTimer: any = null

  state: State = { loading: false, response: null, clusters: [], defaultCluster: '', loadingPercent: 0 }

  componentWillUnmount() {
    if (this.loaderTimer) clearInterval(this.loaderTimer)
  }

  async componentDidMount() {
    try {
      const tenantsRes = await AppSettings.get('TENANTS')
      const tenantsRaw = typeof tenantsRes === 'string' ? JSON.parse(tenantsRes) : (tenantsRes || [])

      const mappedMap = (tenantsRaw as any[]).reduce((m: Map<string, any>, t: any) => {
        const rawLabel = t && (t.NAME || t.name) ? String(t.NAME || t.name).trim() : ''
        if (!rawLabel) return m
        const key = rawLabel.toLowerCase()
        if (!m.has(key)) m.set(key, { raw: t, id: key, label: rawLabel, value: rawLabel })
        return m
      }, new Map<string, any>())
      const mapped: any[] = Array.from(mappedMap.values())

      this.setState({ clusters: mapped })
    } catch (e) {
      this.setState({ clusters: [], defaultCluster: '' })
    }
  }

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    this.props.form.validateFields(async (err, values) => {
      if (err) return

      this.setState({ loading: true, loadingPercent: 5 })
      if (this.loaderTimer) clearInterval(this.loaderTimer)

      this.loaderTimer = setInterval(() => {
        this.setState((prev: State) => {
          const current = Math.round(prev.loadingPercent || 0)
          if (current >= 90) return { loadingPercent: 95 }
           const step = Math.floor(Math.random() * 3) + 1
          const next = Math.min(90, current + step)
          return { loadingPercent: next }
        })
        }, 2500)
      try {
        const request: any = {
          Cluster: (values.cluster || '').toString().trim(),
          Workload: (values.workload || '').toString().trim(),
          WindowDays: Number(values.window_days) || 30,
          TrendHours: Number(values.trend_hours) || 120,
        }
        const res = await getAidraRecommendation(request)
        this.setState({ response: res })
      } catch (ex) {
        message.error('Failed to get recommendation')
      } finally {
        if (this.loaderTimer) {
          clearInterval(this.loaderTimer)
          this.loaderTimer = null
        }
        this.setState({ loadingPercent: 100 })
        setTimeout(() => this.setState({ loading: false, loadingPercent: 0 }), 500)
      }
    })
  }

  render() {
    const { getFieldDecorator } = this.props.form
    const { loading, response, clusters } = this.state
    const clusterInitial = this.state.defaultCluster || undefined

    return (
      <div style={{ padding: 16 }}>
        <div style={{ background: '#fff', padding: 18, border: '1px solid #f0f0f0', borderRadius: 4 }}>
          {this.state.loading && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6, fontSize: 13, color: '#555' }}>Analyzing data...</div>
              <Progress percent={Math.round(this.state.loadingPercent || 0)} strokeWidth={4} />
            </div>
          )}
          <Form layout="vertical" onSubmit={this.handleSubmit}>
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item label="Cluster">
                  {getFieldDecorator('cluster', {
                    initialValue: clusterInitial,
                    rules: [{ required: true, message: 'Select a cluster' }],
                  })(
                    <Select placeholder="Select a cluster">
                      {(clusters || []).map((c: any) => {
                        const key = c.id
                        const val = c.value
                        const label = c.label
                        return <Select.Option key={key} value={val}>{label}</Select.Option>
                      })}
                    </Select>,
                  )}
                </Form.Item>
              </Col>

              <Col xs={24} md={18}>
                <Form.Item label="Application Name" style={{ marginBottom: 8 }}>
                  {getFieldDecorator('workload', {
                    initialValue: '',
                    rules: [{ required: true, message: 'Please enter the application name (e.g., msging-gateway-whatsapp)' }],
                  })(<Input style={{ width: '100%' }} placeholder="Enter the application name (e.g., msging-gateway-whatsapp)" />)}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item label={
                  <span>
                    Window&nbsp;
                    <Tooltip title="Window (days): number of days used as the analysis window">
                      <span style={{ color: '#1890ff' }}>?</span>
                    </Tooltip>
                  </span>
                }>
                  {getFieldDecorator('window_days', {
                    initialValue: 30,
                    rules: [
                      { type: 'number', min: 1, max: 30, message: 'Window must be between 1 and 30' },
                    ],
                  })(<InputNumber min={1} max={30} style={{ width: '100%' }} />)}
                </Form.Item>
              </Col>

              <Col xs={24} md={6}>
                <Form.Item label={
                  <span>
                    Trend&nbsp;
                    <Tooltip title="Trend (hours): hours considered when evaluating recent trends">
                      <span style={{ color: '#1890ff' }}>?</span>
                    </Tooltip>
                  </span>
                }>
                  {getFieldDecorator('trend_hours', {
                    initialValue: 120,
                    rules: [
                      { type: 'number', min: 1, max: 120, message: 'Trend must be between 1 and 120' },
                    ],
                  })(<InputNumber min={1} max={120} style={{ width: '100%' }} />)}
                </Form.Item>
              </Col>

            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>Get Recommendation</Button>
            </Form.Item>
          </Form>
        </div>

        {response && <CloudRecommendationResult response={response} />}
      </div>
    )
  }
}

const CloudRecommendation = Form.create<Props>()(CloudRecommendationBase)

export { CloudRecommendation }
export default CloudRecommendation
